import { useState } from "react";
import { Button, CircularProgress, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { differenceInCalendarDays } from "date-fns";
import { formatDate } from "@Localization/utils";
import { useCTAButtonStyle } from "@View/style/components/tooltipStyles";
import DefaultMultipleRowInput from "@View/components/formInputs/DefaultMultipleRowInput";
import getAPI from "@API/index";

const ACCENT = "#54606F";
const COLOR_ACTIVE = "#43A56C";
const COLOR_INACTIVE = "#EE294C";

const useStyles = makeStyles(() => ({
  statusLabel: {
    marginTop: "-3px",
    marginBottom: "8px",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#A1ADBA",
  },
  status: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "14px 15px",
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
  },
  statusDot: {
    flexShrink: 0,
    width: "10px",
    height: "10px",
    borderRadius: "50%",
  },
  statusText: {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "19px",
    color: "#1A1E24",
  },
  statusNote: {
    marginLeft: "auto",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "16px",
    color: "#A1ADBA",
  },
  warning: {
    marginTop: "12px",
    marginBottom: "16px",
    padding: "10px 15px",
    borderRadius: "8px",
    background: "#F3F5F7",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "16px",
    color: "#54606F",
  },
  meter: {
    marginTop: "20px",
  },
  meterHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "10px",
    marginBottom: "8px",
  },
  meterLabel: {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#A1ADBA",
  },
  meterValue: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#1A1E24",
  },
  meterTrack: {
    height: "6px",
    borderRadius: "3px",
    background: "#E1E6F1",
    overflow: "hidden",
  },
  meterFill: {
    height: "100%",
    borderRadius: "3px",
  },
  lastPeriod: {
    marginTop: "16px",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#A1ADBA",
  },
  activateButton: {
    width: "100%",
    marginTop: "24px",
    marginBottom: "20px",
    borderRadius: "8px",
    padding: "15px 20px",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "17px",
    textTransform: "none",
  },
  activation: {
    marginTop: "24px",
    marginBottom: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #E1E6F1",
  },
  activationTitle: {
    marginBottom: "12px",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "19px",
    color: "#1A1E24",
  },
  activationActions: {
    display: "flex",
    gap: "10px",
  },
  applyButton: {
    flex: 1,
    borderRadius: "8px",
    padding: "15px 20px",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "17px",
    textTransform: "none",
  },
  cancelButton: {
    flex: 1,
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    padding: "15px 20px",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#54606F",
    textTransform: "none",
  },
}));

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime()) || date.getFullYear() < 1900) return null;
  return date;
};

type SubscriptionSectionProps = {
  customer: any;
};

/**
 * Раздел Подписка
 */
const SubscriptionSection = ({ customer }: SubscriptionSectionProps) => {
  const classes = useStyles();
  const ctaStyles = useCTAButtonStyle();
  const { t } = useTranslation();
  const [openActivation, setOpenActivation] = useState(false);
  const [activationKey, setActivationKey] = useState("");
  const [isActivating, setIsActivating] = useState(false);

  const start = parseDate(customer.subscriptionStart);
  const until = parseDate(customer.subscriptionUntil);
  const isActive = Boolean(customer.isActiveSubscription);

  const daysLeft = until ? differenceInCalendarDays(until, new Date()) : null;

  const total = Number(customer.subscriptionRequestAmount) || 0;
  const left = Math.max(Number(customer.requestsLeft) || 0, 0);
  const used = Math.max(total - left, 0);
  const usedPercent = total > 0 ? Math.min(Math.round((used / total) * 100), 100) : 0;

  const termPercent =
    start && until && until.getTime() > start.getTime()
      ? Math.min(
        Math.max(
          Math.round(
            ((Date.now() - start.getTime()) /
              (until.getTime() - start.getTime())) *
            100
          ),
          0
        ),
        100
      )
      : null;

  const period =
    start && until
      ? t("subscriptionSection.period", {
        start: formatDate(start),
        end: formatDate(until),
      })
      : null;

  const handleActivate = async () => {
    if (!activationKey.trim() || isActivating) return;

    setIsActivating(true);
    try {
      await getAPI().request({
        url: "/api/customer/activate-subscription",
        method: "POST",
        data: {
          customerId: customer.id,
          ActivationKey: activationKey.trim(),
        },
        suppressError: false,
      });
      
      window.location.reload();
    } catch (e) {
      
    } finally {
      setIsActivating(false);
    }
  };

  const closeActivation = () => {
    setOpenActivation(false);
    setActivationKey("");
  };

  const renderMeter = (
    label: string,
    value: string,
    percent: number,
    color: string
  ) => (
    <div className={classes.meter}>
      <div className={classes.meterHead}>
        <span className={classes.meterLabel}>{label}</span>
        <span className={classes.meterValue}>{value}</span>
      </div>
      <div className={classes.meterTrack}>
        <div
          className={classes.meterFill}
          style={{ width: `${percent}%`, background: color }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className={classes.statusLabel}>
        {t("subscriptionSection.statusLabel")}
      </div>
      <div className={classes.status}>
        <span
          className={classes.statusDot}
          style={{ background: isActive ? COLOR_ACTIVE : COLOR_INACTIVE }}
        />
        <span className={classes.statusText}>
          {isActive
            ? t("subscriptionSection.active")
            : t("subscriptionSection.inactive")}
        </span>
        {isActive && until && (
          <span className={classes.statusNote}>
            {t("subscriptionSection.untilDate", { date: formatDate(until) })}
            {daysLeft !== null && daysLeft >= 0
              ? `, ${t("subscriptionSection.daysLeft", { days: daysLeft })}`
              : ""}
          </span>
        )}
      </div>

      {/* Расход имеет смысл только при активной подписке: у истёкшей остаток заморожен */}
      {isActive && total > 0 &&
        renderMeter(
          t("subscriptionSection.requests"),
          t("subscriptionSection.leftOf", { left, total }),
          usedPercent,
          ACCENT
        )}

      {isActive && termPercent !== null && period &&
        renderMeter(
          t("subscriptionSection.term"),
          period,
          termPercent,
          ACCENT
        )}

      {!isActive && period && (
        <div className={classes.lastPeriod}>
          {t("subscriptionSection.lastPeriod", { period })}
        </div>
      )}

      {!openActivation ? (
        <Button
          type="button"
          className={`${classes.activateButton} ${ctaStyles.default}`}
          onClick={() => setOpenActivation(true)}
        >
          {t("buttons.Activate")}
        </Button>
      ) : (
        <div className={classes.activation}>
          <div className={classes.activationTitle}>
            {t("modals.activationTitle")}
          </div>

          {isActive && (
            <div className={classes.warning}>
              {t("modals.activationReplaceWarning")}
            </div>
          )}

          <DefaultMultipleRowInput
            label={t("modals.keyLabel")}
            value={activationKey}
            onChange={(value: string) => setActivationKey(value)}
            rows={5}
            disabled={isActivating}
          />

          <div className={classes.activationActions}>
            <Button
              type="button"
              className={classes.cancelButton}
              disabled={isActivating}
              onClick={closeActivation}
            >
              {t("buttons.Cancel")}
            </Button>
            {/* type=button обязателен: раздел живёт внутри формы настроек заказчика */}
            <Button
              type="button"
              className={`${classes.applyButton} ${ctaStyles.default}`}
              disabled={!activationKey.trim() || isActivating}
              onClick={handleActivate}
            >
              {isActivating ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <CircularProgress size={16} color="inherit" />
                  {t("buttons.Apply")}
                </div>
              ) : (
                t("buttons.Apply")
              )}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionSection;
